import { features } from '@/lib/helpers/dashboard'
import { VALID_CURRENCIES } from '~/lib/constants/currencies'
import { getCurrency } from '~/util'

export const state = () => {
  return {
    logos: [],
    selected_logo: null,
    brand: null,
    catalog: [],
    industries: [],
  }
}

export const mutations = {
  setSelectedLogo(state, logo) {
    state.selected_logo = logo
  },

  setCatalog(state, catalog) {
    state.catalog = catalog
  },

  setLogos(state, value) {
    state.logos = value
  },

  addLogos(state, logos) {
    state.logos.push(...logos)
  },

  setBrand(state, brand) {
    state.brand = brand
  },

  setIndustries(state, industries) {
    state.industries = industries
  },

  reset(state) {
    state.logos = []
    state.selected_logo = null
  },
}

export const actions = {
  async fetchCatalog({ commit, rootState }) {
    let currency = getCurrency() || 'usd'

    if (!VALID_CURRENCIES.includes(currency)) {
      currency = 'usd'
    }

    try {
      const {
        data,
      } = await this.$api.get(
        `/api/v2/catalog/${rootState.enterprise.id}/packages`,
        { params: { currency } }
      )

      commit('setCatalog', data)

      return data
    } catch (error) {
      this.$sentry.captureException(error)
      throw error
    }
  },

  refreshLogos({ commit }) {
    commit('setLogos', [])
  },

  // fetches logos from logo-app
  async fetchLogos({ commit, rootState, rootGetters }, options = {}) {
    let { session, flags } = rootState
    try {
      if (!session.wordmark) {
        return
      }

      let keywords = []

      if (rootGetters['session/getKeywords'].length) {
        keywords.push(...rootGetters['session/getKeywords'])
      }

      const opts = JSON.parse(JSON.stringify(options))

      opts.font_styles = options.font_categories
      opts.font_categories = []
      const { data: logos } = await this.$api.post('/api/v2/logos', {
        quantity: 6,
        wordmark: session.wordmark,
        slogan: session.slogan,
        keywords: keywords,
        enable_containers: flags.enable_containers,
        enable_text_casing: true,
        eid: rootState.enterprise.id,
        new_font_endpoint: true,
        icon_vendor: flags.iconfinder_icons ? 'iconfinder' : undefined,
        ...opts,
      })

      // if logos are uneven, pop one off
      // this maintains the two column layout
      if (logos.length % 2 !== 0) {
        logos.pop()
      }
      commit('addLogos', logos)

      return logos
    } catch (error) {
      this.$sentry.captureException(error)
      throw error
    }
  },

  /**
   * Check if the user can upgrade their package.
   *
   * @param {object} pkg A full Package Object from the API
   *
   * @returns {boolean}
   */
  async packageCanUpgrade({ dispatch, getters }, { tier }) {
    // Check the Enterprise first to avoid redundant checks
    if (!getters.featureIsEnabled('upgrade')) {
      return false
    }

    try {
      // Fetch the catalog and Spread it so we don't mutate
      const packages = [...((await dispatch('fetchCatalog')) || [])]
      const index = packages.findIndex(({ code }) => code === tier) + 1

      // If there are no packages above this tier, we're maxed out!
      return packages.slice(index).length > 0
    } catch (error) {
      this.$sentry.captureException(error)

      // Enable the upgrade
      return true
    }
  },

  async fetchIndustries({ commit, rootState }) {
    const res = await this.$api.get('/api/v3/assets/industries')
    const { data } = res

    const duplicateReducer = (acc, industry) => {
      if (acc.find(existing => existing === industry)) {
        return acc
      }

      return [...acc, industry]
    }

    commit(
      'setIndustries',
      data
        .reduce(duplicateReducer, [])
        .map(industry => ({ id: industry, name: industry }))
    )
  },
}

export const getters = {
  /**
   * Checks if the EID contains one or more site addons
   */
  catalogHasWebsiteAddons: ({ catalog }) =>
    catalog.filter(pkg => pkg.type === 'site').length > 0,

  /**
   * Checks if the EID contains one or more account plans
   */
  catalogHasAccountPlans: ({ catalog }) =>
    catalog.filter(pkg => pkg.type === 'plan').length > 0,

  /**
   * Check if a feature is available in a Package.
   *
   * @param {object} logo A full Package Object from the API
   * @param {object} feature A feature Object from the list above
   *
   * @returns {boolean}
   */
  packageHasFeature: ({ catalog }, _getters, rootState, rootGetters) => (
    logo,
    feature
  ) => {
    // Allow all packages to access Dashboard & Upgrades
    const allow = [
      'dashboard',
      'upgrades',
      'site',
      'icon-picker',
      'stock-image-picker',
    ]

    if (allow.includes(feature.product)) {
      return true
    }

    // Depending on where we are in the flow, Tier might not be defined.
    const check = logo.package_code

    const { products } = catalog.find(({ code }) => code === check) || {}

    const findFeatureInProducts = ({ type, code }) => {
      let hasFeature = false

      // Dashboard features all have `type=dashboard`, so check their `code` instead
      if (type === 'dashboard') {
        hasFeature = code === feature.product
      } else {
        hasFeature = type === feature.product
      }

      return hasFeature
    }

    let packageHasFeature = products?.some(findFeatureInProducts)

    if (
      !packageHasFeature &&
      rootGetters['payments/hasAccountSubscription'] &&
      getters['catalogHasAccountPlans']
    ) {
      // The users current package is missing these features, so we should now
      // check their account sub if they have one!
      const accountSub = rootState.payments.subscriptions[0]
      packageHasFeature = accountSub.package.products.some(
        findFeatureInProducts
      )
    }

    return !!packageHasFeature
  },

  /**
   * Check if a Feature is enabled in Flags and the Enterprise.
   *
   * @param {{feature: object|string}}
   */
  featureIsEnabled: (_state, getters, { enterprise, flags }) => feature => {
    let enabled = true
    let feat = feature
    // If the feature is a String, get the full Object
    if (typeof feature === 'string') {
      feat = getters.getFeatureById(feature)
    }

    // There's no feature mapping or no product, use the slug and treat it as a base EID feature instead
    if (!feat?.product) {
      return !!enterprise.features[feat?.slug || feature]
    } else if (feat.flag) {
      // If the feature has a flag, check it ⛳
      // TODO: check if the flag is actually defined
      enabled = !!flags[feat.flag]
    }

    return !!(enabled && enterprise.features[feat.product])
  },

  /**
   * Find a Feature by a slug.
   *
   * @param {slug: string} Slug
   */
  getFeatureById: () => id => features.find(({ slug }) => slug === id),
}
